import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { testimonials } from '@/mocks/manga';

const siteUrl = 'https://hanmanwu.com';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d9peosli2vhsdh2d32rg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
      setFormStatus('error');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩漫屋',
    description: '韩漫屋是国内领先的免费正版韩国漫画在线阅读平台。我们致力于为广大中文读者提供海量优质的韩国漫画资源。',
    about: {
      '@type': 'Organization',
      name: '韩漫屋',
      foundingDate: '2023',
      description: '韩漫屋提供海量免费正版韩国漫画在线阅读，每日更新热门韩漫。',
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们 - 韩漫屋 | 免费正版韩国漫画在线阅读平台"
        description="了解韩漫屋的品牌故事与发展历程。韩漫屋是国内领先的免费正版韩国漫画在线阅读平台，致力于为广大中文读者提供海量优质的韩国漫画资源。"
        keywords="韩漫屋,关于韩漫屋,韩漫屋品牌,韩国漫画平台,正版韩漫"
        canonicalUrl={`${siteUrl}/about`}
      />

      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>

      <main>
        <Navbar />
        <div className="pt-20">
          <div className="w-full px-4 md:px-6">
            <div className="py-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>

            {/* Brand Introduction */}
            <section className="mb-16">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 mb-8 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
                关于韩漫屋
              </h1>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-4">品牌故事</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    韩漫屋创立于2023年，是国内领先的免费正版韩国漫画在线阅读平台。我们怀着对韩国漫画的热爱和对中文读者的承诺，打造了这个集海量资源、优质体验和社区互动于一体的韩漫阅读空间。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    我们与多家韩国顶级漫画平台和知名作者建立了深度合作关系，确保平台上的每一部漫画都经过正版授权。从恋爱甜宠到奇幻冒险，从校园青春到热血战斗，韩漫屋覆盖了几乎所有热门题材，满足不同读者的口味。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们的团队由一群热爱漫画的年轻人组成，包括专业的编辑、翻译和技术人员。我们不仅追求内容的丰富和更新速度，更注重阅读体验的每一个细节——高清画质、流畅加载、舒适排版，只为给您最好的韩漫阅读体验。
                  </p>
                </div>
                <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20cozy%20modern%20reading%20space%20with%20bookshelves%2C%20warm%20ambient%20lighting%2C%20comfortable%20reading%20nook%2C%20digital%20tablet%20displaying%20colorful%20comic%20art%2C%20warm%20peach%20and%20cream%20tones%2C%20inviting%20atmosphere%2C%20professional%20interior%20photography%20style&width=800&height=600&seq=about-brand&orientation=landscape"
                    alt="韩漫屋品牌形象"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
                {[
                  { value: '2023', label: '创立年份' },
                  { value: '2000+', label: '漫画作品' },
                  { value: '50万+', label: '活跃读者' },
                  { value: '98%', label: '好评率' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center p-6 bg-background-100 rounded-xl">
                    <div className="text-2xl md:text-3xl font-bold text-primary-500">{stat.value}</div>
                    <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Reputation / Testimonials */}
            <section id="reputation" className="mb-16">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-8 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {testimonials.map((t, i) => (
                  <div key={i} className="bg-background-100 rounded-xl p-5 md:p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                        <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                        <div className="flex items-center gap-0.5 mt-0.5">
                          {Array.from({ length: t.rating }).map((_, j) => (
                            <i key={j} className="ri-star-fill text-accent-500 text-xs"></i>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{t.comment}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact Form */}
            <section id="contact" className="mb-16">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">有任何问题或合作意向，欢迎随时联系我们</p>

              <div className="max-w-xl mx-auto">
                <form onSubmit={handleSubmit} data-readdy-form className="bg-background-100 rounded-xl p-6 md:p-8 space-y-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">您的姓名</label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full bg-background-50 border border-background-200/70 rounded-lg px-4 py-2.5 text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">电子邮箱</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full bg-background-50 border border-background-200/70 rounded-lg px-4 py-2.5 text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的电子邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      required
                      className="w-full bg-background-50 border border-background-200/70 rounded-lg px-4 py-2.5 text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full bg-background-50 border border-background-200/70 rounded-lg px-4 py-3 text-sm text-foreground-900 placeholder-foreground-400 resize-none outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的留言内容（最多500字）"
                    />
                  </div>

                  {/* Honeypot */}
                  <input type="text" name="phone_alt" className="honeypot-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

                  <button
                    type="submit"
                    className="w-full bg-primary-500 hover:bg-primary-600 text-background-50 py-3 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    发送消息
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-center text-green-600">消息已发送成功！我们会尽快与您取得联系。感谢您的关注！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-center text-red-500">{formError || '提交失败，请稍后再试'}</p>
                  )}
                </form>
              </div>
            </section>
          </div>
        </div>
        <Footer />
      </main>
    </>
  );
}